import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import { MISTING_PHOTOS } from "@/data/mistingPhotos";
import hero from "@/assets/cooling/misting-fan-hero.jpg";
import heroPicture from "@/assets/cooling/misting-fan-hero.jpg?w=640;1024;1600&format=avif;webp;jpeg&as=picture";
import pergolaMistAsset from "@/assets/cooling/misting-pergola-restaurant.jpg.asset.json";
import pedestalFanAsset from "@/assets/cooling/portable-pedestal-misting-fan.jpg.asset.json";
import beamFanAsset from "@/assets/cooling/beam-mounted-misting-fan.jpg.asset.json";
import porchMistAsset from "@/assets/cooling/residential-front-porch-misting.jpg.asset.json";

const pergolaMistImg = pergolaMistAsset.url;
const pedestalFanImg = pedestalFanAsset.url;
const beamFanImg = beamFanAsset.url;
const porchMistImg = porchMistAsset.url;

const FAQS = [
  {
    q: "Why do Texas patios need fan misting instead of misting lines alone?",
    a: "Humidity. In San Antonio, Austin, New Braunfels and Seguin the air already holds moisture, so a fog that just hangs in place feels damp instead of cool. A misting fan pushes the fog out and moves air across you, so the water evaporates the way it is supposed to and you feel a real temperature drop instead of sticky mist.",
  },
  {
    q: "What nozzle size do you use on misting fans?",
    a: "We typically spec smaller .006 nozzles on the fans. The smaller droplet has more surface area relative to its volume, so it evaporates faster in front of the fan. The fan then carries that cooler, drier air out into the seating area instead of leaving it to settle around the line.",
  },
  {
    q: "Can the fans cover a whole patio?",
    a: "Yes. The fans oscillate 90 degrees and can be wall-mounted or placed on pedestals. That oscillation sweeps the mist across the area and keeps the cooled air moving. For larger restaurant decks or ranch patios we put several fans on one high-pressure pump so every seat feels the breeze.",
  },
  {
    q: "Are you an Aeromist dealer?",
    a: "Yes — we are a retail dealer for Aeromist misting fans and high-pressure misting components. We spec the fan, pump, nozzles and tubing for your patio, install it, and service it afterward.",
  },
  {
    q: "Do you install outdoor heaters too?",
    a: "Yes — that lives on our Outdoor Heating & Cooling page. We mount infrared patio heaters, wall-, ceiling- and post-mounted units and coordinate cooling equipment for semi-enclosed outdoor rooms so the space stays comfortable year-round.",
  },
  {
    q: "How much cooler does a misting fan make a patio?",
    a: "On a dry, breezy day a well-designed high-pressure system can pull temperatures down 20-30°F. On a humid Texas afternoon expect a smaller but very noticeable drop in the fan's air stream — which is exactly why we lead with fan-driven systems here instead of ring lines.",
  },
  {
    q: "Will it get my furniture and floor wet?",
    a: "It should not. High-pressure systems atomize water into droplets fine enough to evaporate before they land. Getting that right is a matter of pump pressure, nozzle count, spacing and aim — the part most DIY kits get wrong.",
  },
  {
    q: "What does a misting fan system need?",
    a: "A water source, an outdoor-rated electrical circuit, and the right pump for the number of nozzles. Portable pedestal fans plug in and run off a garden hose; permanent systems get hard-plumbed and wired, with tubing hidden in the pergola, awning or patio-cover frame.",
  },
  {
    q: "Can you add misting or heating to an awning, pergola or patio cover you didn't build?",
    a: "Usually yes. We retrofit fans, misting lines and heaters onto most sound existing structures, and we will tell you honestly when a frame is not a good candidate.",
  },
  {
    q: "Is financing available?",
    a: "Yes — outdoor living projects including cooling, misting and heating qualify for financing through Acorn Finance. Ask us when we quote your project.",
  },
];

const TITLE = "Outdoor Misting Cooling — Patio Misting Systems San Antonio & Austin | Aeromist Dealer";
const DESCRIPTION =
  "Texas fan-first misting with .006 nozzles on Aeromist fans for humid patios. Wall, pedestal and beam-mounted oscillating misting fans across San Antonio, Austin, New Braunfels and Seguin.";

export const Route = createFileRoute("/outdoor-cooling-misting")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESCRIPTION,
      image: hero,
      socialTitle: "Outdoor Misting Cooling — Aeromist Fan Misting Systems for Texas Patios",
      socialDescription:
        "Fan-driven high-pressure misting sized for Texas humidity. Authorized retail Aeromist dealer serving San Antonio, Austin, New Braunfels and Seguin — with outdoor heating handled on our Outdoor Heating & Cooling page.",
      keywords:
        "misting fan San Antonio, misting fans Austin, .006 misting nozzle, Aeromist dealer Texas, patio misting system, high pressure misting fan, outdoor cooling San Antonio, patio heating Austin, infrared patio heater, restaurant patio misting, commercial misting fans, outdoor heating Texas, pergola heater, outdoor misting fan", 
    }),
    scripts: serviceHeadScripts({
      path: "/outdoor-cooling-misting",
      serviceName: "Outdoor Misting Cooling",
      serviceType: "Outdoor cooling, misting fan, high-pressure misting system and infrared patio heater supply and installation",
      breadcrumbName: "Outdoor Misting Cooling",
      description:
        "Aeromist fan misting systems with .006 nozzles, sized for Texas humidity. Wall, pedestal and beam-mounted oscillating misting fans for patios, restaurants, ranches and event spaces across San Antonio, Austin, New Braunfels and Seguin.",
      faqs: FAQS,
    }),
  }),
  component: OutdoorCoolingMistingPage,
});

function OutdoorCoolingMistingPage() {
  return (
    <ServicePage
      currentPath="/outdoor-cooling-misting"
      serviceName="Outdoor Misting Cooling"
      breadcrumbs={[
        { label: "Outdoor Living", to: "/hill-country-series" },
        { label: "Outdoor Misting Cooling" },
      ]}
      keywordOutline={[
        { id: "overview", label: "Texas Fan-First Misting" },
        { id: "why-us", label: "Why Us — Misting & Cooling" },
        { id: "details", label: "Fan Types & Nozzles" },
        { id: "applications", label: "Where We Install" },
        { id: "faqs", label: "Texas Humidity FAQs" },
      ]}
      whyUsPlacement="afterHero"
      eyebrow="★ HILL COUNTRY SERIES™ · OUTDOOR LIVING & PREMIUM SHADE ★"
      title="Outdoor Misting Cooling — Fan Misting for Texas Patios"
      intro="Shade takes the sun off you. A misting fan makes the patio usable in August. In Texas humidity — San Antonio, Austin, New Braunfels and Seguin — we lead with fan-driven misting: smaller .006 nozzles atomize the water in front of an oscillating fan so it evaporates before it lands, and the fan carries that cooler air out into your seating area. No fog bank. No damp furniture. Just moving, breathable cool."
      heroImage={heroPicture}
      heroImageClassName="opacity-60"
      heroOverlayClassName="bg-gradient-to-r from-navy-deep/85 via-navy-deep/55 to-navy-deep/10"
      preContent={
        <section className="bg-navy-deep py-14 text-cream md:py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-[minmax(0,260px)_1fr] lg:px-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <SeriesBadge slug="hill-country-series" size="lg" priority />
              <div>
                <div className="font-display text-xs uppercase tracking-[0.3em] text-cream/70">
                  Primary Badge
                </div>
                <div className="mt-1 font-display text-lg font-bold">Hill Country Series™</div>
                <div className="text-sm text-cream/80">Outdoor Living &amp; Premium Shade</div>
              </div>
            </div>
            <div>
              <h2 className="font-display text-3xl font-bold leading-tight md:text-4xl">
                In Texas humidity, fan misting is the cooling system that actually works
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-cream/85">
                Our sales team specs misting fans for Texas conditions — not desert mist lines. We
                use smaller .006 nozzles on the fans because the finer droplet evaporates faster in
                front of the fan, and the moving air carries the cooled air out into the patio instead
                of letting it settle. When humidity is already high — as it often is in San Antonio,
                Austin, New Braunfels and Seguin — adding more water to still air just makes the
                space feel damp. A fan pushes the mist through a larger volume of air, so you feel
                the temperature drop instead of a fog bank.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-cream/85">
                We are a <strong>retail dealer for Aeromist</strong> — oscillating and pedestal
                misting fans, high-pressure pumps, stainless nozzles, tubing and fittings — and we
                design the system into your shade structure so lines and wiring are hidden, not
                zip-tied on. When the weather turns, we add infrared patio heaters to the same
                structures so the space pays for itself twelve months a year.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/estimate-quotes"
                  className="border-2 border-cream bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white hover:bg-red-deep"
                >
                  Get an Outdoor Misting Cooling Quote →
                </Link>
                <Link
                  to="/hill-country-series"
                  className="border-2 border-cream px-5 py-3 font-display text-sm uppercase tracking-wider hover:bg-cream hover:text-navy-deep"
                >
                  Explore Outdoor Living →
                </Link>
              </div>
            </div>
          </div>
        </section>
      }
      features={[
        "Texas fan-first misting with smaller .006 nozzles",
        "Misting fans for humid Central & South Texas patios",
        "Retail Aeromist dealer — fans, pumps, nozzles & tubing",
        "Pairs with outdoor heating for year-round use",
        "Portable pedestal fans or permanent hard-plumbed systems",
        "Misting lines hidden inside awning & pergola framing",
        "Residential patios, restaurant decks, ranches & events",
        "Installed and serviced by our own Seguin-based crew",
      ]}
      body={[
        {
          heading: "1. Texas Fan-First Misting — Why Fans Beat Misting Lines Here",
          text: "Our rep sizes misting for real Texas weather, not dry-climate assumptions. In higher-humidity areas like San Antonio, Austin, New Braunfels and Seguin, a traditional mist line adds more water to air that is already holding moisture. The fog does not evaporate fast enough, so the patio feels damp instead of cool. We lead with misting fans because the fan does two jobs: it atomizes the water in front of it, and it moves that cooled air across the seating area so you feel the drop.",
          bullets: [
            "Smaller .006 nozzles create finer droplets that evaporate faster in front of the fan",
            "The fan carries cooler, drier air out into the patio instead of letting mist settle",
            "Oscillating fans sweep 90 degrees for coverage across seating, bars and work areas",
            "Wall-mounted or pedestal placement fits the layout without blocking walkways",
            "In high humidity, moving air works where still mist lines only add moisture",
            "Fan misting pairs with drop screens, awnings and pergolas to hold the cool air in place",
          ],
          image: {
            src: pedestalFanImg,
            alt: "Portable oscillating pedestal misting fan spraying fine mist across a residential courtyard patio",
            caption:
              "Portable oscillating pedestal misting fan — fan-driven cooling sized for Texas humidity instead of desert air.",
          },
          cta: { label: "Ask for a misting fan quote", href: "/estimate-quotes#general" },
        },
        {
          heading: "2. Fan Types — Pedestal, Wall, Post & Multi-Fan Systems",
          text: "Every misting fan we quote is built around the same idea: put the right amount of finely atomized water in front of moving air. The difference is how it mounts and how large an area it needs to cool.",
          bullets: [
            "Oscillating pedestal fan — portable, rolls where you need it, runs off a hose and a plug",
            "Wall-mounted oscillating fan — out of the walkway, aimed at seating or a bar",
            "Post- or beam-mounted fan — built into a pergola, patio cover or awning frame",
            "Multi-fan pump system — 2 to 8 fans on one high-pressure pump for restaurant decks, barns and shops",
            "All fan kits include the pump, stainless nozzles, high-pressure line and push-lock fittings",
          ],
          image: {
            src: beamFanImg,
            alt: "Ceiling-mounted misting fan running over a covered outdoor dining table at dusk",
            caption:
              "Ceiling- and beam-mounted misting fan — mounted into the patio cover so nothing sits in the walkway and the lines stay hidden.",
          },
          cta: { label: "Add motorized drop screens", href: "/exterior-vertical-shades" },
        },
        {
          heading: "3. Nozzle & Pressure — The .006 Difference",
          text: "Nozzle size is what separates a cooling mist from a wet drizzle. We typically spec .006 nozzles on misting fans because the smaller opening produces a finer droplet. A finer droplet has more surface area relative to its volume, so it flashes into vapor faster — exactly what you want when humidity is already working against evaporation. Combined with high pressure and the fan's air movement, the water cools the air instead of landing on your furniture.",
          bullets: [
            ".006 nozzles produce finer droplets that evaporate before they drift down",
            "High-pressure pump (typically 1,000 PSI) forces water through the tiny orifice",
            "Fan air movement extends the evaporation window and pushes cooled air outward",
            "Fewer nozzles, better aim and proper spacing prevent oversaturation",
            "Stainless nozzles resist clogging from hard Central Texas water",
          ],
          image: {
            src: porchMistImg,
            alt: "Fine mist drifting across a covered residential front porch with stone columns on a clear hot day",
            caption:
              "Residential porch system — fog kept fine and in motion under the cover so the space feels cooler instead of damp.",
          },
        },
        {
          heading: "4. Aeromist Dealership — Factory Equipment, Local Install",
          text: "Texas Made Shade is a retail dealer for Aeromist, a manufacturer of high-pressure misting fans and misting components. That means you buy factory equipment with factory parts behind it, spec'd and installed by our own crew in Seguin instead of guessed at from a big-box kit. We size the pump to the nozzle count, quote the exact fan and tubing package, install it, and service it later — one company for the shade and the cooling.",
          bullets: [
            "Factory Aeromist fans, pumps, nozzles, tubing and fittings",
            "Systems spec'd for your patio, not a one-size box kit",
            "Replacement parts and service through us, not a call center",
            "Quotes list fan count, pump pressure, nozzle count and tubing run",
            "Heater circuits planned with the same frame so controls are in one place",
            "Financing available through Acorn Finance",
          ],
          image: {
            src: pergolaMistImg,
            alt: "High-pressure misting nozzles fogging a shaded pergola patio where guests are seated at a table",
            caption:
              "Aeromist dealer install — high-pressure misting built into a pergola so guests stay comfortable through a Texas afternoon.",
          },
          cta: { label: "Ask for a misting quote", href: "/estimate-quotes#general" },
        },
        {
          heading: "5. Installation — Pumps, Nozzles & Hidden Lines",
          text: "A misting system feels premium or cheap based almost entirely on the install. Because we fabricate the awnings, pergola covers and screens ourselves, the water and power get planned with the structure: tubing inside the beams, the pump tucked out of sight with service access, controls where you actually stand.",
          bullets: [
            "High-pressure tubing routed inside frames, beams and posts",
            "Pump mounted discreetly with a filter and service access",
            "Nozzle spacing and aim set so nothing gets soaked",
            "Outdoor-rated power and a dedicated water tie-in",
            "Retrofits onto most sound existing patios and structures",
            "Optional automation: timers, temperature triggers and phone control through the Lone Star Series™",
          ],
        },
        {
          heading: "6. Pairing With Outdoor Heating & Cooling",
          text: "Cooling is only half of a year-round patio. Once summer is handled, the chill becomes the problem — and a covered or screened space can go further than heat alone. Infrared patio heaters, mini-split and spot cooling for semi-enclosed outdoor rooms, and the controls that tie them together all live on our Outdoor Heating & Cooling page, and we plan them on the same beams and circuits as your misting system.",
          bullets: [
            "Infrared radiant heat mounted to the same frames as the misting fans",
            "Cooling equipment for covered, screened and semi-enclosed rooms",
            "Motorized drop screens that hold cooled or heated air in the space",
            "One set of controls for misting, heat, screens and lighting",
            "Quoted together so the wiring and water are planned once",
          ],
          cta: { label: "See Outdoor Heating & Cooling", href: "/outdoor-hvac" },
        },
      ]}
      applicationsEyebrow="APPLICATIONS"
      applicationsHeading="Where We Install Misting Fans & Outdoor Cooling"
      applications={[
        "Backyard patios & outdoor kitchens",
        "Pergolas & louvered structures",
        "Retractable & fabric awnings",
        "Pool, spa & cabana areas",
        "Restaurant & bar patios",
        "Breweries, food trucks & vendor booths",
        "Barns, stalls & livestock areas",
        "Ranch shops & working alleys",
        "Warehouse doors & loading areas",
        "Event tents & venue spaces",
        "Sports sidelines & dugouts",
        "Car washes & service bays",
        "Outdoor seating & covered dining areas",
        "Hotel pool decks & fire-pit lounges",
      ]}
      letsTalkImageSrc={hero}
      letsTalkImageAlt="Black oscillating misting fan cooling a shaded Texas Hill Country patio at golden hour"
      gallerySection={{
        eyebrow: "Aeromist Installations",
        title: "Outdoor Misting & Cooling Photo Gallery",
        intro:
          "Real Aeromist high-pressure misting, misting fan, fogging, and portable cooling installations — patios, breweries, pools, play areas, and working yards.",
        categories: ["High-Pressure Misting", "Misting Fans", "Fogging & Effects", "Portable Cooling"],
        columns: 3,
        images: MISTING_PHOTOS.map((p) => ({
          src: p.src,
          alt: p.alt,
          label: p.label,
          caption: p.caption,
          category: p.category,
        })),
      }}
      gallerySectionPlacement="beforeLetsTalk"
      faqs={FAQS}
    />
  );
}
